import React, { useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Link } from 'expo-router';

import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import useTranslation from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import AppStoreBadge from '@/components/Icons/AppStoreBadge';
import MacAppButton from '@/components/Buttons/MacAppButton';
import GooglePlayBadge from '@/components/Icons/GooglePlayBadge';
import { isDarkTheme } from '@/constants/Colors';

import {
  APP_STORE_URL, GOOGLE_PLAY_URL, openStoreUrl,
} from './PublicFooter/publicFooterStoreLinks';
import { safeAccessibilityRole } from '@/constants/utils/a11y';
import { FOOTER_NAV_COLUMNS } from '@/constants/publicNavLinks';
import { ACCENT, ACCENT_BRIGHT, ACCENT_RGB } from '@/components/Landing/landingTokens';

// Phthalo brand green — same accent as the trust checkmarks below the CTA.
const CTA_SHADOW = `rgba(${ACCENT_RGB}, 0.45)`;
const CTA_SHADOW_HOVER = `rgba(${ACCENT_RGB}, 0.55)`;
const STAR_COLOR = '#FBBF24';
const CHECK_COLOR = ACCENT;

const isWeb = Platform.OS === 'web';

/**
 * Public footer for marketing pages and unauthenticated app surfaces.
 * Conversion-first hero, static link columns, legal strip — no live catalog fetches.
 *
 * Every destination here goes through `expo-router`'s `Link` rather than
 * `router.push`. React Native Web turns a `Pressable` into `<div role="link">`,
 * which is invisible to crawlers — so this footer, the densest set of internal
 * links on the site, was passing no link equity to any of the pages it lists.
 *
 * `Link asChild` passes `href` down to the `Pressable`, and React Native Web
 * renders any View with an `href` as a real `<a>`, so the styling is unchanged
 * and crawlers get a followable link.
 */

/**
 * A link that keeps its press and hover styling.
 *
 * `Link asChild` merges props through Radix's `Slot`, which combines `style`
 * with an object spread. That only works for a plain object: an array arrives
 * as `{0: …, 1: …}` and React Native Web throws trying to set indexed
 * properties on a CSSStyleDeclaration, and a `({ pressed }) => …` function
 * spreads to `{}`, dropping every style with no error at all. So interaction
 * state is tracked here and the result is flattened before it reaches the
 * `Pressable`.
 */
function FooterLink({ href, style, children, ...pressableProps }) {
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);

  const resolved = typeof style === 'function' ? style({ pressed, hovered }) : style;

  return (
    <Link href={href} asChild>
      <Pressable
        {...pressableProps}
        onPressIn={() => setPressed(true)}
        onPressOut={() => setPressed(false)}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        style={StyleSheet.flatten(resolved) || {}}
      >
        {children}
      </Pressable>
    </Link>
  );
}

const TRUST_BULLETS = ['Start for free', 'No credit card required', 'Cancel anytime'];

/**
 * @param showHero Set false when the page already ends with its own
 *   call-to-action, so the footer does not repeat it.
 */
function PublicFooter({ variant = 'default', showHero = true, excludeRoutes = [] }) {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const isEditorial = variant === 'editorial';
  const isDark = isDarkTheme(theme);

  const bottomLinks = useMemo(() => ([
    { route: '/privacy-policy', label: t('common.privacyPolicy') },
    { route: '/terms-of-service', label: t('common.termsOfService') },
    { route: '/terms-of-sale', label: t('common.termsOfSale') },
    { route: '/contact', label: t('common.contact') },
    { route: '/feedback', label: 'Feedback' },
    { route: '/dmca', label: t('common.dmca') },
  ]), [t]);

  return (
    <View
      style={[
        styles.root,
        {
          backgroundColor: colors.solid_color_primary,
          borderTopColor: colors.border_color_secondary,
        },
      ]}
      accessibilityRole={safeAccessibilityRole('contentinfo')}
    >
      <View style={styles.inner}>
        {showHero ? <View style={styles.hero}>
          <View style={styles.socialProof}>
            {!isEditorial && (
              <View style={styles.stars}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Icon key={i} name="star" size={13} color={STAR_COLOR} solid />
                ))}
              </View>
            )}
            <Text size="xs" color="secondary" style={styles.socialProofText}>
              {isEditorial ? 'Everything you need. One place.' : 'Loved by creators & builders worldwide'}
            </Text>
          </View>

          <Text
            weight="bold"
            color="primary"
            style={[
              styles.heroHeadline,
              { fontSize: isXS ? 32 : 46, lineHeight: isXS ? 38 : 54 },
            ]}
          >
            {isEditorial ? 'Ready to make something?' : "All the AI you'll ever need."}
          </Text>
          <Text
            color="secondary"
            style={[
              styles.heroSub,
              { fontSize: isXS ? 16 : 18, lineHeight: isXS ? 24 : 28 },
            ]}
          >
            {isEditorial
              ? 'Start free. Bring every model and medium with you.'
              : '100+ models, every major agent and tool — in one workspace, under one login. Stop paying for five subscriptions and start shipping.'}
          </Text>

          <FooterLink
            href="/signup"
            accessibilityRole="link"
            accessibilityLabel={t('pages.public.signUp')}
            style={({ pressed, hovered }) => [
              styles.primaryCta,
              isXS && styles.primaryCtaFull,
              isEditorial && {
                backgroundColor: isDark ? '#F5F5F0' : '#0A0A0A',
                boxShadow: 'none',
              },
              !isEditorial && isWeb && hovered && !pressed && {
                backgroundColor: ACCENT_BRIGHT,
                transform: [{ translateY: -1 }],
                boxShadow: `0 16px 36px ${CTA_SHADOW_HOVER}`,
              },
              pressed && { opacity: 0.92, transform: [{ translateY: 0 }] },
            ]}
          >
            <Text
              style={[
                styles.primaryCtaText,
                isEditorial && { color: isDark ? '#0A0A0A' : '#FAFAF7' },
              ]}
            >
              Start free
            </Text>
            <Icon name="arrow-right" size={14} color={isEditorial && isDark ? '#0A0A0A' : '#ffffff'} solid />
          </FooterLink>

          <View style={styles.checkRow}>
            {TRUST_BULLETS.map((label) => (
              <View key={label} style={styles.checkItem}>
                <View style={[styles.checkBubble, { backgroundColor: `${CHECK_COLOR}22` }]}>
                  <Icon name="check" size={9} color={CHECK_COLOR} solid />
                </View>
                <Text size="xs" color="secondary" style={styles.checkLabel}>
                  {label}
                </Text>
              </View>
            ))}
          </View>

          {!isEditorial && <View style={styles.heroBadges}>
            <Pressable
              onPress={() => openStoreUrl(APP_STORE_URL)}
              accessibilityRole="link"
              accessibilityLabel="Download on the App Store"
              style={({ hovered, pressed }) => [
                styles.badgePress,
                isWeb && hovered && !pressed && { transform: [{ scale: 1.03 }] },
                pressed && { opacity: 0.8 },
              ]}
            >
              <AppStoreBadge width={isXS ? 140 : 152} />
            </Pressable>
            <MacAppButton scale={isXS ? 0.92 : 1} />
            <Pressable
              onPress={() => openStoreUrl(GOOGLE_PLAY_URL)}
              accessibilityRole="link"
              accessibilityLabel="Get it on Google Play"
              style={({ hovered, pressed }) => [
                styles.badgePress,
                isWeb && hovered && !pressed && { transform: [{ scale: 1.03 }] },
                pressed && { opacity: 0.8 },
              ]}
            >
              <GooglePlayBadge width={isXS ? 156 : 170} />
            </Pressable>
          </View>}
        </View> : null}

        <View
          style={[
            styles.cols,
            { borderTopColor: colors.border_color_secondary },
            // Without the hero above it, this rule would just echo the footer's
            // own top border a few pixels away.
            !showHero && { borderTopWidth: 0, paddingTop: 0 },
          ]}
        >
          {FOOTER_NAV_COLUMNS.map((col) => (
            <View key={col.title} style={styles.col}>
              <Text
                size="xxs"
                weight="bold"
                style={[styles.colTitle, { color: colors.text_color_tertiary }]}
              >
                {col.title}
              </Text>
              {col.links.filter(l => !excludeRoutes.includes(l.route)).map((l) => (
                <FooterLink
                  key={l.route}
                  href={l.route}
                  accessibilityRole="link"
                  accessibilityLabel={l.label}
                  style={({ pressed }) => [styles.colLink, pressed && { opacity: 0.7 }]}
                >
                  <Text size="xs" color="secondary" style={styles.colLinkText}>
                    {l.label}
                  </Text>
                </FooterLink>
              ))}
            </View>
          ))}
        </View>

        <View
          style={[
            styles.bottom,
            { borderTopColor: colors.border_color_secondary },
            isXS && styles.bottomStacked,
          ]}
        >
          <Text size="xxs" color="secondary" style={styles.bottomMeta}>
            <Text size="xxs" color="secondary" style={{ fontWeight: '600' }}>Gab AI Inc</Text>
            {'  ·  '}© {new Date().getFullYear()}
          </Text>

          <View style={styles.bottomLinks}>
            {bottomLinks.map((l, idx, arr) => (
              <View key={l.route} style={styles.bottomLinkWrap}>
                <FooterLink
                  href={l.route}
                  accessibilityRole="link"
                  style={({ pressed }) => (pressed ? { opacity: 0.7 } : null)}
                >
                  <Text size="xxs" color="secondary" style={styles.bottomLink}>
                    {l.label}
                  </Text>
                </FooterLink>
                {idx < arr.length - 1 ? (
                  <Text size="xxs" color="tertiary" style={styles.dot}>·</Text>
                ) : null}
              </View>
            ))}
          </View>
        </View>
      </View>
    </View>
  );
}

PublicFooter.displayName = 'PublicFooter';

const styles = StyleSheet.create({
  root: {
    width: '100%',
    alignSelf: 'stretch',
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  inner: {
    width: '100%',
    maxWidth: 1280,
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingTop: 56,
    paddingBottom: 18,
  },
  hero: {
    alignItems: 'center',
    paddingBottom: 56,
  },
  socialProof: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginBottom: 18,
  },
  stars: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  socialProofText: {
    letterSpacing: 0.1,
  },
  heroHeadline: {
    textAlign: 'center',
    letterSpacing: -1.4,
    marginBottom: 14,
    maxWidth: 760,
  },
  heroSub: {
    textAlign: 'center',
    maxWidth: 600,
    marginBottom: 30,
    opacity: 0.95,
  },
  primaryCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    minWidth: 220,
    backgroundColor: ACCENT,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease, opacity 0.15s ease',
        boxShadow: `0 8px 22px ${CTA_SHADOW}`,
      },
      default: {},
    }),
  },
  primaryCtaFull: {
    width: '100%',
    minWidth: 0,
  },
  primaryCtaText: {
    color: '#ffffff',
    fontWeight: '700',
    fontSize: 17,
    letterSpacing: -0.2,
  },
  checkRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 18,
    rowGap: 10,
    marginTop: 18,
    maxWidth: 560,
  },
  checkItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  checkBubble: {
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkLabel: {
    letterSpacing: -0.05,
  },
  heroBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    flexWrap: 'wrap',
    marginTop: 28,
  },
  badgePress: {
    ...Platform.select({
      web: { cursor: 'pointer', transition: 'transform 0.15s ease, opacity 0.15s ease' },
      default: {},
    }),
  },
  cols: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: 28,
    columnGap: 24,
    alignItems: 'flex-start',
    paddingTop: 36,
    paddingBottom: 28,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  col: {
    flexBasis: 130,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 130,
    maxWidth: 240,
  },
  colTitle: {
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  colLink: {
    paddingVertical: 3,
    ...Platform.select({ web: { cursor: 'pointer' }, default: {} }),
  },
  colLinkText: {
    lineHeight: 18,
    letterSpacing: -0.1,
  },
  bottom: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingTop: 14,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  bottomStacked: {
    alignItems: 'flex-start',
  },
  bottomMeta: {
    letterSpacing: 0.4,
    fontVariant: ['tabular-nums'],
  },
  bottomLinks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    flexShrink: 1,
    minWidth: 0,
    rowGap: 6,
  },
  bottomLinkWrap: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  bottomLink: {
    letterSpacing: 0.1,
  },
  dot: {
    marginHorizontal: 8,
    opacity: 0.6,
  },
});

export default React.memo(PublicFooter);
